// ATLAS · SHAPE map — ORACLE's spinning disc, now driven by saved data.
// One spoke per enabled SHAPE sector (plus custom spokes), five concentric
// rings = S.T.A.G.E. (Step innermost → Endeavour outermost). A node is solid
// when that stage is defined for that sector and hollow when it is missing,
// so the disc shows every journey at once (horizontal orientation) while the
// selected spoke is lifted out (spec §4 "connected to actual saved data").
import React, { useEffect, useRef, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import Svg, { Circle, Ellipse, G, Line, Text as SvgText } from 'react-native-svg';
import { Pause, Play } from 'lucide-react-native';
import type { Sector } from '../../domain/olmps';
import type { Spoke } from '../../olmps/sectorConfig';
import { stageStatus, type AtlasSpine, type StageKey } from '../../olmps/atlas-data';
import { useTheme, makeThemedStyles } from '../../v10/theme';

const SIZE = 320;
const CX = SIZE / 2;
const CY = SIZE / 2;
const RX_OUTER = SIZE * 0.31;
const TILT = 0.46;
/** Inner → outer ring = Step → Endeavour. */
const RINGS: { stage: StageKey; ratio: number }[] = [
  { stage: 'S', ratio: 0.2 },
  { stage: 'T', ratio: 0.4 },
  { stage: 'A', ratio: 0.6 },
  { stage: 'G', ratio: 0.8 },
  { stage: 'E', ratio: 1.0 },
];
const NODE_R = 5;
const SPIN_SECONDS = 40;

const rad = (deg: number) => (deg * Math.PI) / 180;
const angleFor = (i: number, count: number) => 90 + (360 / count) * i;
const pt = (rx: number, ry: number, deg: number) => ({ x: CX + rx * Math.cos(rad(deg)), y: CY + ry * Math.sin(rad(deg)) });

export function AtlasDisc({
  spokes,
  spine,
  selected,
  onSelect,
  scoreOf,
}: {
  spokes: Spoke[];
  spine: AtlasSpine;
  selected: Sector | null;
  onSelect: (s: Sector) => void;
  /** Aim-orb Alignment Score text per sector ("3" / "—"), shown after the spoke label. */
  scoreOf?: (s: Sector) => string;
}) {
  const styles = useStyles();
  const { t } = useTheme();
  const count = Math.max(1, spokes.length);
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(true);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!spinning) return;
    const DEG_PER_MS = 360 / (SPIN_SECONDS * 1000);
    let last = Date.now();
    const tick = () => {
      const now = Date.now();
      setRotation((r) => (r + (now - last) * DEG_PER_MS) % 360);
      last = now;
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [spinning]);

  return (
    <View style={styles.wrap}>
      <Svg width={SIZE} height={SIZE * 0.56} viewBox={`0 ${SIZE * 0.22} ${SIZE} ${SIZE * 0.56}`}>
        {RINGS.map((r) => (
          <Ellipse
            key={r.stage}
            cx={CX}
            cy={CY}
            rx={RX_OUTER * r.ratio}
            ry={RX_OUTER * r.ratio * TILT}
            stroke={t.gold}
            strokeWidth={0.7}
            strokeOpacity={0.35}
            fill="none"
          />
        ))}
        {spokes.map((spoke, i) => {
          const deg = angleFor(i, count) + rotation;
          const node = spoke.key ? spine[spoke.key] : null;
          const isSel = !!spoke.key && spoke.key === selected;
          const outer = pt(RX_OUTER, RX_OUTER * TILT, deg);
          const label = pt(RX_OUTER + 10, RX_OUTER * TILT + 12, deg);
          const cos = Math.cos(rad(deg));
          // Anchor the label away from the centre so it never sits on the outer node.
          const anchor = cos > 0.3 ? 'start' : cos < -0.3 ? 'end' : 'middle';
          const dy = Math.sin(rad(deg)) > 0.3 ? 10 : Math.sin(rad(deg)) < -0.3 ? -4 : 3;
          const back = Math.sin(rad(deg)) < 0;
          const press = spoke.key ? () => onSelect(spoke.key as Sector) : undefined;
          return (
            <G key={spoke.id} onPress={press} opacity={back && !isSel ? 0.7 : 1}>
              <Line
                x1={CX}
                y1={CY}
                x2={outer.x}
                y2={outer.y}
                stroke={spoke.color}
                strokeWidth={isSel ? 2.4 : 1}
                strokeOpacity={isSel ? 0.95 : 0.45}
              />
              {RINGS.map((r) => {
                const p = pt(RX_OUTER * r.ratio, RX_OUTER * r.ratio * TILT, deg);
                const defined = node ? stageStatus(node, r.stage).defined : false;
                const rr = NODE_R * (isSel ? 1.3 : 1) * (back ? 0.85 : 1);
                return (
                  <Circle
                    key={r.stage}
                    cx={p.x}
                    cy={p.y}
                    r={rr}
                    fill={defined ? spoke.color : t.navy}
                    stroke={defined ? t.ivory : spoke.color}
                    strokeWidth={defined ? 1 : 1.4}
                  />
                );
              })}
              {/* generous hit target on the label */}
              <Circle cx={label.x} cy={label.y} r={18} fill="transparent" />
              <SvgText
                x={label.x}
                y={label.y + dy}
                fontSize={isSel ? 11 : 9.5}
                fontWeight="800"
                fill={isSel ? t.goldBright : spoke.color}
                textAnchor={anchor}
              >
                {spoke.label.toUpperCase()}
                {spoke.key && scoreOf ? ` ${scoreOf(spoke.key as Sector)}` : ''}
              </SvgText>
            </G>
          );
        })}
        <Circle cx={CX} cy={CY} r={SIZE * 0.04} fill={t.navyRaised} stroke={t.gold} strokeWidth={1.5} />
      </Svg>
      <Pressable
        onPress={() => setSpinning((v) => !v)}
        hitSlop={8}
        accessibilityLabel={spinning ? 'Pause map' : 'Spin map'}
        style={styles.spinBtn}
      >
        {spinning ? <Pause size={13} color={t.ivorySoft} /> : <Play size={13} color={t.ivorySoft} />}
      </Pressable>
    </View>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    wrap: { alignItems: 'center' },
    legendRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 8 },
    legend: { flexShrink: 1, fontSize: 10.5, color: t.ivoryFaint, textAlign: 'center' },
    spinBtn: { padding: 4, borderRadius: 999, borderWidth: 1, borderColor: t.navyLine },
  }),
);
